// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import type { Persona } from '../../stores/shellStore';
import { usePersona } from '../../hooks/usePersona';
import { useFlagOn } from '../../hooks/useFlagOn';
import PageTabs from './PageTabs';

/**
 * Power Flow is first, matching the AI Summit mock's sub-nav, and it takes the
 * place of the type-grouped Asset Topology rather than joining it — see
 * docs/power-flow/power-flow-single-line-diagram.md §11. The old segment still
 * routes (it redirects to Power Flow) so nobody's bookmark 404s, but it is
 * not a tab. Every persona sees it: an operator reads the one-line too.
 */
const SITE_SECTIONS: {
  key: string;
  label: string;
  segment: string;
  roles?: Persona[];
}[] = [
  { key: 'power-flow', label: 'Power Flow', segment: 'power-flow' },
  { key: 'floor-plan', label: 'Floor Plan', segment: 'floor-plan' },
  { key: 'asset-directory', label: 'Asset Directory', segment: 'asset-directory' },
  { key: 'procedures', label: 'Procedures', segment: 'procedures' },
];

/**
 * The Asset Directory keeps its search, domain filter and open asset in the
 * query string (AB#30707). The tab bar remembers that query per site while the
 * section is on screen, so coming back from Floor Plan lands on the same view
 * instead of the bare path. Session-scoped: a new browser tab starts clean.
 */
const ASSET_DIRECTORY_SEARCH_KEY = (siteId: string) => `helios.siteTabs.${siteId}.asset-directory`;

/** Site section tab bar — thin wrapper over PageTabs. */
export default function SiteTabs() {
  const { site_id = '' } = useParams<{ site_id: string }>();
  const { pathname, search } = useLocation();
  const { persona } = usePersona();
  const proceduresOn = useFlagOn('siteProceduresEnabled');

  const assetDirectoryPath = `/site/${site_id}/asset-directory`;
  const onAssetDirectory = pathname === assetDirectoryPath;
  useEffect(() => {
    if (onAssetDirectory) sessionStorage.setItem(ASSET_DIRECTORY_SEARCH_KEY(site_id), search);
  }, [onAssetDirectory, site_id, search]);
  const assetDirectorySearch = onAssetDirectory
    ? search
    : (sessionStorage.getItem(ASSET_DIRECTORY_SEARCH_KEY(site_id)) ?? '');

  const tabs = SITE_SECTIONS.filter(s => s.key !== 'procedures' || proceduresOn).map(s => ({
    key: s.key,
    label: s.label,
    to: `/site/${site_id}/${s.segment}`,
    search: s.key === 'asset-directory' ? assetDirectorySearch : undefined,
    roles: s.roles,
  }));
  return (
    <PageTabs tabs={tabs} ariaLabel="Site sections" testIdPrefix="site-tab" persona={persona} />
  );
}
